import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { CHAPTERS, BAND, IMAGES } from "../lib/content";
import { FadeUp, MaskReveal } from "./Reveal";
import { Tilt } from "./Tilt";

export default function Manifesto() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section
      id="about"
      ref={ref}
      data-testid="about-section"
      className="relative bg-[var(--bg)] px-6 md:px-12 py-28 md:py-44"
    >
      <div className="mx-auto max-w-[1500px]">
        <FadeUp className="mb-16 md:mb-28 flex items-center gap-6">
          <span className="h-px w-12 bg-[var(--gold)]" />
          <span className="font-mono text-[11px] tracking-[0.35em] text-[var(--gold-ink)] uppercase">
            Σχετικά / About
          </span>
        </FadeUp>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-x-8 gap-y-16 items-start">
          {/* Big statement */}
          <div className="md:col-span-7">
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-[4.4rem] leading-[1.05] text-[var(--fg)]">
              <MaskReveal>Αναγεννημένοι μουσικοί</MaskReveal>
              <MaskReveal delay={0.08} className="text-[var(--gold-ink)]">
                <span className="italic">που ταξιδεύουν</span>
              </MaskReveal>
              <MaskReveal delay={0.16}>στη λατρεία.</MaskReveal>
            </h2>
            <FadeUp delay={0.2} className="mt-10 max-w-xl">
              <p className="font-body text-lg text-[var(--muted)] leading-relaxed">
                {BAND.intro}
              </p>
            </FadeUp>
          </div>

          {/* Band portrait */}
          <div className="md:col-span-5 md:row-span-2 md:pl-8">
            <FadeUp y={60}>
              <Tilt max={8}>
                <div className="relative overflow-hidden rounded-sm" style={{ transform: "translateZ(30px)" }}>
                  <motion.div style={{ y: imgY }} className="will-change-transform">
                    <img
                      src={IMAGES.poster}
                      alt="ΤΑΞΙΔΙΩΤΕΣ Gospel Music Band"
                      className="w-full object-cover kenburns"
                    />
                  </motion.div>
                  <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-[var(--line-gold)]" />
                </div>
              </Tilt>
              <p className="mt-4 font-mono text-[10px] tracking-[0.3em] text-[var(--muted)] uppercase">
                {BAND.handle} · {BAND.city}
              </p>
            </FadeUp>
          </div>

          {/* Numbered chapters */}
          <div className="md:col-span-7 mt-4 md:mt-10 space-y-px border-t border-[var(--line)]">
            {CHAPTERS.map((c, i) => (
              <FadeUp key={c.no} delay={i * 0.05}>
                <div className="group grid grid-cols-[auto_1fr] gap-6 md:gap-12 border-b border-[var(--line)] py-8 transition-colors duration-500 hover:bg-[var(--surface)]">
                  <span className="font-mono text-sm text-[var(--gold-ink)] pt-2">
                    ({c.no})
                  </span>
                  <div>
                    <h3 className="font-serif text-2xl md:text-3xl text-[var(--fg)] mb-3 group-hover:translate-x-2 transition-transform duration-500">
                      {c.title}
                    </h3>
                    <p className="font-body text-[var(--muted)] leading-relaxed max-w-lg">
                      {c.text}
                    </p>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
